import { Channel, pickVariant, type MusicTrack, type SoundManifest } from "odyssey-shared";
import type { ContextLike, GainLike, MediaLike } from "./graph.ts";

/**
 * Music (ADR-085 decision 13): each screen of the page is a music scene, a folder and the purpose its tracks are
 * chosen by; the scene's tracks play as a shuffled playlist, never the same track twice in a row, crossfading over
 * `CROSSFADE_SECONDS` into the next as one ends and into another scene's when the scene changes. Two media elements
 * stream into the Music channel through their own gains, so a whole track is never decoded. Setting the scene that
 * already plays changes nothing, so a world swap or a reconnect never restarts the music. The background mute pauses
 * the elements and resumes them where they were. A scene with no track fades the music out; a track that fails moves to
 * the next, and a scene whose every track fails in a row stops.
 */

/**
 * The page's music scenes.
 */
export const MusicScene = { Title: "title", Select: "select", World: "world" } as const;
export type MusicScene = (typeof MusicScene)[keyof typeof MusicScene];

/**
 * Where each scene's tracks come from: the folder, and the purpose its tracks' names carry, null for any.
 */
export const MUSIC_SCENES: Readonly<Record<MusicScene, { readonly folder: string; readonly purpose: string | null }>> = Object.freeze({
  title: { folder: "music/title", purpose: "main-theme" },
  select: { folder: "music/title", purpose: "login-menu" },
  world: { folder: "music/shared/adventuring", purpose: null },
});

/**
 * Seconds one track fades into the next.
 */
export const CROSSFADE_SECONDS = 2.5;

/**
 * A scene's tracks: those of its folder whose purpose matches, or every track of the folder when none does.
 *
 * @param manifest The manifest.
 * @param scene The scene.
 * @returns The tracks; none when the folder has none.
 */
export function tracksFor(manifest: SoundManifest, scene: MusicScene): readonly MusicTrack[] {
  const spec = MUSIC_SCENES[scene];
  const tracks = manifest.music[spec.folder] ?? [];
  if (spec.purpose === null) {
    return tracks;
  }
  const matching = tracks.filter((track) => track.purpose === spec.purpose);
  return matching.length > 0 ? matching : tracks;
}

/**
 * What the music asks of the page.
 */
export interface MusicHost {
  /** The context, once unlocked. */
  readonly context: () => ContextLike | null;
  /** The Music channel's input, once unlocked. */
  readonly input: (channel: typeof Channel.Music) => GainLike | null;
  /** Makes a media element. */
  readonly media: () => MediaLike | null;
  /** The URL of a track's first playable encoding, or null. */
  readonly urlOf: (track: MusicTrack) => string | null;
  /** The roll a track is picked by. */
  readonly random: () => number;
  /** Told the track that starts, or null when the music stops. */
  readonly onTrack?: (title: string | null) => void;
}

/**
 * One of the two players: its element and its gain.
 */
interface Deck {
  readonly media: MediaLike;
  readonly gain: GainLike;
}

/**
 * The page's music.
 */
export class MusicDirector {
  private manifest: SoundManifest = { sounds: {}, music: {} };
  private wanted: MusicScene | null = null;
  private playingScene: MusicScene | null = null;
  private decks: [Deck, Deck] | null = null;
  private live: 0 | 1 = 0;
  private lastTrack = -1;
  private readonly failedTracks = new Set<number>();
  private advancing = false;
  private paused = false;

  /**
   * @param host What the music asks of the page.
   */
  public constructor(private readonly host: MusicHost) {}

  /**
   * Takes the manifest the page loaded, and starts the wanted scene if it was waiting for it.
   *
   * @param manifest The manifest.
   */
  public setManifest(manifest: SoundManifest): void {
    this.manifest = manifest;
    this.start();
  }

  /**
   * Asks for a scene's music: crossfades to it unless it already plays; before the context exists, it starts once it
   * does.
   *
   * @param scene The scene.
   */
  public scene(scene: MusicScene): void {
    this.wanted = scene;
    this.start();
  }

  /**
   * Called once the context exists: makes the decks and starts the wanted scene.
   */
  public unlocked(): void {
    this.start();
  }

  /**
   * Pauses both decks or resumes the live one, for the background mute and for maintenance.
   *
   * @param silent True to pause.
   */
  public silence(silent: boolean): void {
    this.paused = silent;
    const deck = this.decks?.[this.live];
    if (deck === undefined || this.playingScene === null) {
      return;
    }
    if (silent) {
      for (const each of this.decks ?? []) {
        each.media.pause();
      }
    } else {
      void deck.media.play().catch(() => undefined);
    }
  }

  /**
   * Starts the wanted scene when it differs from the one playing and the page can play it.
   */
  private start(): void {
    if (this.wanted === null || this.wanted === this.playingScene) {
      return;
    }
    if (tracksFor(this.manifest, this.wanted).length === 0) {
      this.stop();
      return;
    }
    if (this.ensureDecks() === null) {
      return;
    }
    this.playingScene = this.wanted;
    this.lastTrack = -1;
    this.failedTracks.clear();
    this.next();
  }

  /**
   * The two decks, made once the context exists.
   *
   * @returns The decks, or null before the context or where no media element can be made.
   */
  private ensureDecks(): [Deck, Deck] | null {
    if (this.decks !== null) {
      return this.decks;
    }
    const context = this.host.context();
    const input = this.host.input(Channel.Music);
    if (context === null || input === null) {
      return null;
    }
    const made: Deck[] = [];
    for (const index of [0, 1] as const) {
      const media = this.host.media();
      if (media === null) {
        return null;
      }
      media.crossOrigin = "anonymous";
      media.preload = "auto";
      const gain = context.createGain();
      gain.gain.value = 0;
      context.createMediaElementSource(media).connect(gain);
      gain.connect(input);
      media.addEventListener("timeupdate", () => this.near(index));
      media.addEventListener("ended", () => this.ended(index));
      media.addEventListener("error", () => this.failed(index));
      made.push({ media, gain });
    }
    this.decks = made as [Deck, Deck];
    return this.decks;
  }

  /**
   * Crossfades from the live deck to the scene's next track on the other deck.
   */
  private next(): void {
    const decks = this.decks;
    const context = this.host.context();
    const scene = this.playingScene;
    if (decks === null || context === null || scene === null) {
      return;
    }
    const tracks = tracksFor(this.manifest, scene);
    const picked = pickVariant(tracks.length, this.lastTrack, this.host.random());
    let index = picked;
    while (this.failedTracks.has(index) && this.failedTracks.size < tracks.length) {
      index = (index + 1) % tracks.length;
    }
    const track = tracks[index];
    const url = track === undefined ? null : this.host.urlOf(track);
    if (track === undefined || url === null) {
      return;
    }
    this.lastTrack = index;
    const outgoing = decks[this.live];
    this.live = this.live === 0 ? 1 : 0;
    const incoming = decks[this.live];
    const now = context.currentTime;
    outgoing.gain.gain.cancelScheduledValues(now);
    outgoing.gain.gain.setTargetAtTime(0, now, CROSSFADE_SECONDS / 3);
    const fading = outgoing.media;
    setTimeout(() => {
      if (decks[this.live].media !== fading) {
        fading.pause();
      }
    }, CROSSFADE_SECONDS * 1000 * 2);
    incoming.media.src = url;
    incoming.gain.gain.cancelScheduledValues(now);
    incoming.gain.gain.value = 0;
    incoming.gain.gain.setTargetAtTime(1, now, CROSSFADE_SECONDS / 3);
    this.advancing = false;
    this.host.onTrack?.(track.title);
    if (!this.paused) {
      void incoming.media.play().catch(() => undefined);
    }
  }

  /**
   * Fades the live deck out and forgets the scene playing, for a scene with no track or one whose every track failed;
   * asking for a scene afterwards starts it afresh.
   */
  private stop(): void {
    this.playingScene = null;
    const decks = this.decks;
    const context = this.host.context();
    if (decks === null || context === null) {
      return;
    }
    const live = decks[this.live];
    live.gain.gain.cancelScheduledValues(context.currentTime);
    live.gain.gain.setTargetAtTime(0, context.currentTime, CROSSFADE_SECONDS / 3);
    const fading = live.media;
    setTimeout(() => {
      if (this.playingScene === null || decks[this.live].media !== fading) {
        fading.pause();
      }
    }, CROSSFADE_SECONDS * 1000 * 2);
    this.host.onTrack?.(null);
  }

  /**
   * Moves past a live track that failed to load or play, never picking it again until a track plays, and stops once every
   * track of the scene has failed, so a scene of broken tracks never loops through them.
   *
   * @param deck The deck that failed.
   */
  private failed(deck: 0 | 1): void {
    const scene = this.playingScene;
    if (deck !== this.live || scene === null) {
      return;
    }
    this.failedTracks.add(this.lastTrack);
    if (this.failedTracks.size >= tracksFor(this.manifest, scene).length) {
      this.stop();
      return;
    }
    this.advancing = true;
    this.next();
  }

  /**
   * Starts the next track as the live one nears its end.
   *
   * @param deck The deck that reported its time.
   */
  private near(deck: 0 | 1): void {
    const media = this.decks?.[deck].media;
    if (deck !== this.live || media === undefined || this.advancing || !Number.isFinite(media.duration)) {
      return;
    }
    if (media.currentTime > 0) {
      this.failedTracks.clear();
    }
    if (media.duration - media.currentTime <= CROSSFADE_SECONDS) {
      this.advancing = true;
      this.next();
    }
  }

  /**
   * Starts the next track when the live one ended without a crossfade.
   *
   * @param deck The deck that ended.
   */
  private ended(deck: 0 | 1): void {
    if (deck === this.live && !this.advancing) {
      this.advancing = true;
      this.next();
    }
  }
}
